import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { DataTablePage } from './models/api-response.model';
import { DataTableRequest, HierarchyColumnsResponse } from './models/data-table.model';
import { TenantSelectionService } from '../../components/tenant-select/tenant-select.service';
import { toApiColumnFiltersPayload } from '../../../core/filters/filter.types';

/**
 * Generic Data Table API service.
 * Wraps /data-table/{data_table_name} endpoints.
 */
@Injectable({ providedIn: 'root' })
export class DataTableApiService {
  private http = inject(HttpClient);
  private tenantSelection = inject(TenantSelectionService);
  private inFlightDataTableRequests = new Map<string, Observable<DataTablePage<unknown>>>();
  private hierarchyColumnsCache = new Map<string, Observable<HierarchyColumnsResponse>>();

  private static readonly GROWTH_SEGMENT_TABLES = new Set<string>([
    'customer-growth-segments',
    'product-growth-segments',
  ]);

  /**
   * Growth-segment endpoints REQUIRE both windows (their classification
   * is computed from the period-over-period delta), while the generic
   * `DataTableRequest` treats `prior_start` / `prior_end` as optional.
   * When the caller didn't supply a prior window, fall back to the
   * current window so the request still satisfies the server-side
   * `ComparisonWindowRequest` contract.
   *
   * Also collapses the legacy 16-bucket `column_filters` representation
   * into the new op-tagged flat-list shape required by gf-insights-api
   * PR #356 (GF-4038).  The internal UI representation stays unchanged.
   */
  private normalizeRequestForTable(
    tableName: string,
    request: DataTableRequest
  ): Record<string, unknown> {
    const { column_filters, ...rest } = request;
    const base: Record<string, unknown> = { ...rest };
    if (column_filters) {
      base['column_filters'] = toApiColumnFiltersPayload(column_filters);
    }

    if (!DataTableApiService.GROWTH_SEGMENT_TABLES.has(tableName)) {
      return base;
    }

    const { prior_start, prior_end, current_start, current_end } = request;
    return {
      ...base,
      current_start,
      current_end,
      prior_start: prior_start ?? current_start,
      prior_end: prior_end ?? current_end,
    };
  }

  private buildRequestKey(
    tableName: string,
    page: number,
    size: number,
    normalizedRequest: Record<string, unknown>,
    tenantScopeKey: string
  ): string {
    return `${tenantScopeKey}|${tableName}|${page}|${size}|${JSON.stringify(normalizedRequest)}`;
  }

  private normalizeHierarchyColumnsResponse(raw: unknown): HierarchyColumnsResponse {
    const asRecord = (value: unknown): Record<string, unknown> | null =>
      value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
    const toHierarchyColumn = (entry: unknown) => {
      const item = asRecord(entry);
      return {
        backendField: String(item?.['backendField'] ?? item?.['backend_field'] ?? ''),
        uiHeader: String(item?.['uiHeader'] ?? item?.['ui_header'] ?? ''),
      };
    };
    const toColumnArray = (value: unknown) =>
      (Array.isArray(value) ? value : [])
        .map(toHierarchyColumn)
        .filter((entry) => entry.backendField && entry.uiHeader);

    const payload = asRecord(raw) ?? {};
    const hierarchy =
      asRecord(payload['hierarchyColumns']) ?? asRecord(payload['hierarchy_columns']) ?? {};

    return {
      tenantKey: String(payload['tenantKey'] ?? payload['tenant_key'] ?? ''),
      hierarchyColumns: {
        salesRep: toColumnArray(hierarchy['salesRep'] ?? hierarchy['sales_rep']),
        customer: toColumnArray(hierarchy['customer']),
        product: toColumnArray(hierarchy['product']),
      },
    };
  }

  /**
   * Fetch a data table by name.
   * @param tableName Backend-supported table name (e.g. 'sales-reps')
   * @param request Request body containing start/end dates, filters, ordering
   */
  getDataTable<T = unknown>(
    tableName: string,
    request: DataTableRequest,
    page: number,
    size: number
  ): Observable<DataTablePage<T>> {
    const safePage = Number.isFinite(page) ? Math.max(0, Math.trunc(page)) : 0;
    const safeSize = Number.isFinite(size) ? Math.max(1, Math.trunc(size)) : 1;
    const params = `?page=${safePage}&size=${safeSize}`;
    const normalizedRequest = this.normalizeRequestForTable(tableName, request);
    const switchContext = this.tenantSelection.getTenantSwitchContext();
    const tenantScopeKey = switchContext.tenantKey || this.tenantSelection.getCurrentTenantKey();
    const requestKey = this.buildRequestKey(
      tableName,
      safePage,
      safeSize,
      normalizedRequest,
      tenantScopeKey
    );
    const inFlight = this.inFlightDataTableRequests.get(requestKey);
    if (inFlight) {
      return inFlight as Observable<DataTablePage<T>>;
    }

    const request$ = this.http
      .post<
        DataTablePage<T>
      >(`${environment.api}/data-table/${tableName}${params}`, normalizedRequest)
      .pipe(
        finalize(() => {
          this.inFlightDataTableRequests.delete(requestKey);
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    this.inFlightDataTableRequests.set(requestKey, request$ as Observable<DataTablePage<unknown>>);
    return request$;
  }

  exportDataTable(tableName: string, request: DataTableRequest): Observable<HttpResponse<Blob>> {
    const normalizedRequest = this.normalizeRequestForTable(tableName, request);
    return this.http.post(
      `${environment.api}/data-table/${tableName}/export/csv`,
      normalizedRequest,
      {
        responseType: 'blob',
        observe: 'response',
      }
    );
  }

  getHierarchyColumns(tenantKey: string): Observable<HierarchyColumnsResponse> {
    const scopedTenantKey = (tenantKey || '').trim() || this.tenantSelection.getCurrentTenantKey();
    const cached = this.hierarchyColumnsCache.get(scopedTenantKey);
    if (cached) {
      return cached;
    }

    const request$ = this.http.get<unknown>(`${environment.api}/data-table/hierarchy-columns`).pipe(
      map((response) => this.normalizeHierarchyColumnsResponse(response)),
      catchError((err) => {
        this.hierarchyColumnsCache.delete(scopedTenantKey);
        return throwError(() => err);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.hierarchyColumnsCache.set(scopedTenantKey, request$);
    return request$;
  }
}
